import { ExternalLink } from 'lucide-react'

import { AppReviewActions } from './AppReviewActions'

import { AppReview } from '@/api/app-client'
import { Badge } from '@/components/ui/badge'
import { DataTableColumnHeader } from '@/components/ui/data-table-column-header'
import { TableColumn } from '@/models'
import { unsensitiveFilter } from '@/utils'

const STATUS_BADGE_CLASSES: Record<AppReview['review_status'], string> = {
  pending:
    'border-amber-500/50 bg-amber-500/10 text-amber-600 dark:text-amber-400',
  approved:
    'border-green-500/50 bg-green-500/10 text-green-600 dark:text-green-400',
  rejected: 'border-red-500/50 bg-red-500/10 text-red-600 dark:text-red-400'
}

export const AppReviewsTableColumns: TableColumn<AppReview>[] = [
  {
    id: 'app_id',
    accessorKey: 'app_id',
    displayName: 'App ID',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="App ID" />
    ),
    enableResizing: true,
    filterFn: unsensitiveFilter
  },
  {
    id: 'version',
    accessorKey: 'version',
    displayName: 'Version',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Version" />
    ),
    enableResizing: false,
    filterFn: unsensitiveFilter
  },
  {
    id: 'org_id',
    accessorKey: 'org_id',
    displayName: 'Owner Org ID',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Owner Org ID" />
    ),
    enableResizing: true,
    filterFn: unsensitiveFilter
  },
  {
    id: 'review_status',
    accessorKey: 'review_status',
    displayName: 'Status',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => (
      <Badge
        className={STATUS_BADGE_CLASSES[row.original.review_status]}
        variant="outline"
      >
        {row.original.review_status}
      </Badge>
    ),
    enableResizing: false,
    filterFn: unsensitiveFilter
  },
  {
    id: 'requested_by',
    accessorKey: 'requested_by',
    displayName: 'Requested by',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Requested by" />
    ),
    enableResizing: true,
    sortUndefined: 1,
    filterFn: unsensitiveFilter
  },
  {
    id: 'requested_at',
    accessorKey: 'requested_at',
    displayName: 'Requested at',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Requested at" />
    ),
    cell: ({ row }) => {
      if (!row.original.requested_at) {
        return ''
      }

      return new Date(row.original.requested_at).toLocaleString()
    },
    sortUndefined: -1,
    enableGlobalFilter: false,
    enableColumnFilter: false
  },
  {
    id: 'technical_contact',
    accessorKey: 'technical_contact',
    displayName: 'Technical contact',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Technical contact" />
    ),
    enableResizing: true,
    sortUndefined: 1,
    filterFn: unsensitiveFilter
  },
  {
    id: 'marketing_contact',
    accessorKey: 'marketing_contact',
    displayName: 'Marketing contact',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Marketing contact" />
    ),
    enableResizing: true,
    sortUndefined: 1,
    filterFn: unsensitiveFilter
  },
  {
    id: 'demo_url',
    accessorKey: 'demo_url',
    displayName: 'Demo',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Demo" />
    ),
    cell: ({ row }) =>
      row.original.demo_url ? (
        <a
          className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
          href={row.original.demo_url}
          onClick={(e) => e.stopPropagation()}
          rel="noreferrer"
          target="_blank"
        >
          Demo <ExternalLink className="w-3 h-3" />
        </a>
      ) : (
        ''
      ),
    enableGlobalFilter: false,
    enableColumnFilter: false,
    enableSorting: false
  },
  {
    id: 'actions',
    displayName: 'Actions',
    header: () => null,
    cell: ({ row }) => <AppReviewActions review={row.original} />,
    enableSorting: false,
    enableHiding: false,
    enableResizing: false,
    enableGlobalFilter: false,
    enableColumnFilter: false
  }
]
